import { memo, useMemo } from 'react';
import { useAuthContext } from '@/src/auth/hooks';
import { hasManagerPrivilege } from '@/src/auth/context/jwt/utils';

import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import {
  PUBLIC_NODE_ENV,
  NEXT_PUBLIC_CMS_DOMAIN,
  NEXT_PUBLIC_KIOSK_DOMAIN,
} from 'src/config-global';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

const getKioskDomain = (): string => {
  const envKiosk = NEXT_PUBLIC_KIOSK_DOMAIN?.trim();
  if (envKiosk) return envKiosk.replace(/\/$/, '');

  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:1103';
    }
    if (hostname.includes('dev-') || hostname.includes('dev.')) {
      return 'https://dev-kiosk.thehyoosik.me';
    }
    return 'https://kiosk.thehyoosik.me';
  }
  if (PUBLIC_NODE_ENV === 'production') {
    return 'https://kiosk.thehyoosik.me';
  }
  return 'https://dev-kiosk.thehyoosik.me';
};

const getCmsDomain = (): string => {
  const envCms = NEXT_PUBLIC_CMS_DOMAIN?.trim();
  if (envCms) return envCms.replace(/\/$/, '');

  const kioskDomain = (NEXT_PUBLIC_KIOSK_DOMAIN || '').trim();
  if (kioskDomain.includes('localhost') || kioskDomain.includes('127.0.0.1')) {
    return 'http://localhost:1104';
  }
  if (kioskDomain.includes('dev-kiosk')) {
    return 'https://dev-cms.thehyoosik.me';
  }
  if (kioskDomain.includes('kiosk.thehyoosik.me')) {
    return 'https://cms.thehyoosik.me';
  }
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:1104';
    }
    if (hostname.includes('dev-') || hostname.includes('dev.')) {
      return 'https://dev-cms.thehyoosik.me';
    }
    return 'https://cms.thehyoosik.me';
  }
  if (PUBLIC_NODE_ENV === 'production') {
    return 'https://cms.thehyoosik.me';
  }
  return 'https://dev-cms.thehyoosik.me';
};

interface SpeechBubbleProps {
  show: boolean;
  onClose: () => void;
}

const SpeechBubble = ({ show, onClose }: SpeechBubbleProps) => {
  const { currentAccommodation, member } = useAuthContext();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const content = useMemo(() => {
    const isManager = hasManagerPrivilege(member);
    const kioskDomain = getKioskDomain();
    const cmsDomain = getCmsDomain();

    const ITEMS = [
      {
        label: 'KIOSK',
        // 여기에 accommodationId 쿼리 파라미터 추가
        href:
          isManager && currentAccommodation?.id
            ? `${kioskDomain}/dashboard?accommodationId=${currentAccommodation.id}`
            : `${kioskDomain}/dashboard`,
      },
      {
        label: 'CMS',
        // 여기에 accommodationId 쿼리 파라미터 추가
        href:
          isManager && currentAccommodation?.id
            ? `${cmsDomain}/dashboard?accommodationId=${currentAccommodation.id}`
            : `${cmsDomain}/dashboard`,
      },
      {
        label: '도움말',
        href: `https://www.notion.so/EUREKA-Pro-2105c774788a80cdb557e6c9ba16d90a?source=copy_link`,
      },
      {
        label: '업데이트 노트',
        href: `https://www.notion.so/2325c774788a80f29017e531ecc76305?v=2325c774788a80d3a808000c7d7c122a&source=copy_link`,
      },
    ];

    return (
      show && (
        <Stack
          spacing={0.5}
          sx={{
            position: 'absolute',
            top: '80%',
            minWidth: 160,
            p: 1,
            borderRadius: 1,
            backgroundColor: isDark ? theme.palette.background.paper : '#E9ECEE',
            border: isDark ? `1px solid ${theme.palette.divider}` : 'none',
            boxShadow: isDark ? (theme.customShadows?.z8 || 3) : 3,
            zIndex: 1300,
          }}
        >
          {ITEMS.map((item) => (
            <Stack
              key={item.label}
              direction="row"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(item.href, '_blank', 'noopener,noreferrer');
                onClose();
              }}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1,
                py: 0.5,
                borderRadius: 0.75,
                cursor: 'pointer',

                '&:hover': {
                  backgroundColor: isDark ? 'action.hover' : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: isDark ? 'text.primary' : '#212B36',
                }}
              >
                {item.label}
              </Typography>

              <Iconify icon="eva:diagonal-arrow-right-up-fill" width={16} sx={{ color: 'primary.main' }} />
            </Stack>
          ))}
        </Stack>
      )
    );
  }, [show, onClose, member, currentAccommodation, isDark, theme]);

  return content || null;
};

export default memo(SpeechBubble);
